import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { useNavigate } from '@tanstack/react-router';
import { useSession } from '@/lib/auth-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { AppSidebar } from '@/components/app-sidebar';
import { ModeToggle } from '@/components/mode-toggle';
import {
  RiDatabase2Line,
  RiAddLine,
  RiSparklingLine,
  RiArrowRightLine,
  RiTimeLine,
  RiPlayCircleLine,
  RiGuideLine,
} from '@remixicon/react';
import { api, Database as DatabaseType } from '@/lib/api';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { useOnboardingStore } from '@/stores/onboarding-store';

export function DashboardPage() {
  const { data: session } = useSession();
  const navigate = useNavigate();
  const [databases, setDatabases] = useState<DatabaseType[]>([]);
  const [isLoadingDatabases, setIsLoadingDatabases] = useState(true);
  const { openOnboarding, hasCompletedOnboarding } = useOnboardingStore();

  useEffect(() => {
    loadDatabases();
  }, [session?.user?.id]);

  // Auto-open onboarding for users who haven't completed it yet
  useEffect(() => {
    if (!hasCompletedOnboarding && !isLoadingDatabases) {
      // Small delay to ensure the page has loaded
      const timer = setTimeout(() => {
        openOnboarding();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [hasCompletedOnboarding, isLoadingDatabases, openOnboarding]);

  const loadDatabases = async () => {
    if (!session?.user?.id) return;

    try {
      setIsLoadingDatabases(true);
      const result = await api.listDatabases(session.user.id);
      setDatabases(result.databases);
    } catch (error: any) {
      setDatabases([]);
    } finally {
      setIsLoadingDatabases(false);
    }
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="sticky top-0 flex h-16 shrink-0 items-center gap-2 border-b bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/80 px-4 z-10 shadow-sm">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="h-4 mr-2" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>Dashboard</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="ml-auto">
            <ModeToggle />
          </div>
        </header>

        <main className="flex-1 p-6 overflow-auto md:p-8 lg:p-10">
          <div className="max-w-6xl mx-auto">
            {/* Hero Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <div className="flex flex-col items-center gap-8 lg:flex-row lg:gap-12">
                {/* Left side - Content */}
                <div className="flex-1 text-center lg:text-left">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="inline-flex items-center gap-2 px-4 py-2 mb-6 text-sm font-medium border rounded-full shadow-sm bg-gradient-to-r from-primary/10 to-primary/5 text-primary border-primary/10"
                  >
                    <RiSparklingLine className="size-4" />
                    <span>Welcome back!</span>
                  </motion.div>

                  <h1 className="mb-4 text-4xl font-bold leading-tight text-transparent md:text-5xl lg:text-6xl bg-gradient-to-br from-foreground via-foreground to-foreground/60 bg-clip-text">
                    Hey {session?.user?.name?.split(' ')[0] || 'there'} 👋
                  </h1>

                  <p className="max-w-xl mx-auto mb-8 text-lg leading-relaxed md:text-xl text-muted-foreground lg:mx-0">
                    Your visual database management platform. Connect, query, and visualize your data with ease.
                  </p>

                  <div className="flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
                    <Button
                      size="lg"
                      onClick={() => navigate({ to: '/databases' })}
                      className="h-12 gap-2 px-6 text-base"
                    >
                      <RiAddLine className="size-5" />
                      Connect Database
                    </Button>
                    {databases.length > 0 && (
                      <Button
                        size="lg"
                        variant="outline"
                        onClick={() => navigate({ to: '/databases' })}
                        className="h-12 gap-2 px-6 text-base group"
                      >
                        View All Databases
                        <RiArrowRightLine className="transition-transform size-5 group-hover:translate-x-1" />
                      </Button>
                    )}
                    <Button
                      size="lg"
                      variant="ghost"
                      onClick={() => openOnboarding()}
                      className="h-12 gap-2 px-6 text-base"
                    >
                      <RiGuideLine className="size-5" />
                      Getting Started Guide
                    </Button>
                  </div>
                </div>

                {/* Right side - Illustration */}
                <div className="flex justify-center flex-1">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    className="relative w-full max-w-md aspect-square"
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <img
                        src="/dashboard-m.svg"
                        alt="Database illustration"
                        className="object-contain w-full h-full p-8 invert-0 dark:invert-0"
                        onError={(e) => {
                          // Fallback to icon if image doesn't exist
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.parentElement!.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-48 h-48 text-primary/40" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg></div>';
                        }}
                      />
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>

            {/* Stats Overview - Minimal */}
            {databases.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex items-center justify-center gap-8 mb-10 text-center"
              >
                <div>
                  <p className="text-3xl font-bold text-foreground">{databases.length}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Databases</p>
                </div>
                <div className="w-px h-8 bg-border" />
                <div>
                  <p className="text-3xl font-bold text-muted-foreground">-</p>
                  <p className="mt-1 text-xs text-muted-foreground">Queries</p>
                </div>
                <div className="w-px h-8 bg-border" />
                <div>
                  <p className="text-3xl font-bold text-muted-foreground">-</p>
                  <p className="mt-1 text-xs text-muted-foreground">Avg Time</p>
                </div>
              </motion.div>
            )}

            {/* Connected Databases - Compact Liquid Glass Style */}
            {databases.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold">Connected Databases</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate({ to: '/databases' })}
                    className="gap-1 text-sm"
                  >
                    View all
                    <RiArrowRightLine className="size-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {databases.slice(0, 6).map((db, index) => (
                    <motion.div
                      key={db.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + index * 0.05 }}
                    >
                      <Card
                        className="group cursor-pointer bg-card/50 backdrop-blur-xl border-border/60 hover:border-primary/40 hover:bg-card/70 transition-all duration-300 hover:scale-[1.02] hover:shadow-soft-md"
                        onClick={() => navigate({ to: `/query/${db.id}` })}
                      >
                        <CardContent className="p-5">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center flex-shrink-0 transition-all duration-300 shadow-sm rounded-xl size-11 bg-primary/10 group-hover:bg-primary/20">
                              <RiDatabase2Line className="size-5 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold truncate">{db.name}</p>
                              <p className="text-xs truncate text-muted-foreground mt-0.5">
                                {db.type.toUpperCase()} • {db.type === 'sqlite' ? 'Local' : db.host}
                              </p>
                            </div>
                            <RiArrowRightLine className="transition-all duration-300 opacity-0 size-4 text-muted-foreground group-hover:opacity-100 group-hover:translate-x-0.5" />
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
